import { authenticatedFetch } from './base'

/**
 * Get current patient
 *
 * @returns {*} - response containing current patient model
 */
export const fetchPatient = () => {
	return authenticatedFetch(`/api/patient`, { method: 'get' })
		.then(function(response) {
			if (response.status === 200) {
				return response.json()
			}
		})
		.then(function(user) {
			return user
		})
}

/**
 * Get current state of multi-factor authorization for current patient's owner
 *
 * @param {string} data - encrypted model with case data
 * @param {string} email - patient's email
 * @returns {*} - response indicating if MFA is enabled
 */
export const fetchOwnerMFAState = (data, email) => {

    let url = '/api/patient/ownerMFAState';

	if (data) {
		url += `?data=${encodeURIComponent(data)}`;
    }

    if (email) {
		url += `?email=${encodeURIComponent(email)}`;
    }

	return authenticatedFetch(url, { method: 'get' })
        .then(function (response) {
            if (response.status === 200) {
                return response.json()
            }
        })
        .then(function (response) {
			return response
        })
}

/**
 * Update patient data
 *
 * @param {object} patient - data to update
 * @returns {*} - response containing data for the "Welcome" step
 */
export const updatePatient = (patient) => {
	return authenticatedFetch(`/api/patient`, {
		method: 'put',
		headers: { 'content-type': 'application/json' },
		body: JSON.stringify(patient)
	})
		.then(function(response) {
			if (response.status === 200) {
				return response.json()
			}
		})
		.then(function(user) {
			return user
		})
}

/**
 * Update language for the patient
 *
 * @param {number} id - patient ID
 * @param {string} language - new language
 * @returns {*} - response containing patient's model
 */
export const updatePatientLanguage = (id, language) => {
	return authenticatedFetch(`/api/patient/${id}/language/${language}`, {
		method: 'post',
		headers: { 'content-type': 'application/json' },
		body: JSON.stringify({ langauge: language })
	})
		.then(function(response) {
			if (response.status === 200) {
				return response.json()
			}
		})
		.then(function(user) {
			return user
		})
}

/**
 * Update language for the patient
 *
 * @param {number} id - patient ID
 * @param {number} language - new language ID
 * @returns {*} - response containing patient's model
 */
export const updatePatientLanguageById = (id, language) => {
	return authenticatedFetch(`/api/patient/${id}/languageId/${language}`, {
		method: 'post',
		headers: { 'content-type': 'application/json' },
		body: JSON.stringify({ langauge: language })
	})
		.then(function(response) {
			if (response.status === 200) {
				return response.json()
			}
		})
		.then(function(user) {
			return user
		})
}

/**
 * Get team background image URL for current patient.
 *
 * @returns {*} - response containing background image URL
 */
export const fetchTeamBackground = () => {
	return authenticatedFetch(`/api/file/team/backgroundUrl`, { method: 'get' })
		.then(function(response) {
			if (response.status === 200) {
				return response.json()
			}
		})
		.then(function(data) {
			return data
		})
}

/**
 * Get team background image URL by patient's email
 *
 * @param {string} email - patient's email
 * @returns {*} - response containing background image URL
 */
export const fetchTeamBackgroundByEmail = (email) => {
	return authenticatedFetch(`/api/file/team/backgroundUrl/email?data=${encodeURIComponent(email)}`, { method: 'get' })
		.then(function(response) {
			if (response.status === 200) {
				return response.json()
			}
		})
		.then(function(data) {
			return data
		})
}

/**
 * Get team background image URL by encrypted link.
 *
 * @param {string} encryptedData - encrypted data from the link
 * @returns {*} - response containing background image URL
 */
export const fetchTeamBackgroundByEncryptedLink = (encryptedData) => {
	return authenticatedFetch(`/api/file/team/backgroundUrl/encrypted?data=${encodeURIComponent(encryptedData)}`, { method: 'get' })
		.then(function(response) {
			if (response.status === 200) {
				return response.json()
			}
		})
		.then(function(data) {
			return data
		})
}

/**
 * Get team logo image URL for current patient.
 *
 * @returns {*} - response containing logo image URL
 */
export const fetchTeamLogo = () => {
	return authenticatedFetch(`/api/file/team/logoUrl`, { method: 'get' })
		.then(function(response) {
			if (response.status === 200) {
				return response.json()
			}
		})
		.then(function(data) {
			return data
		})
}

/**
 * Get team logo image URL by patient's email
 *
 * @param {string} email - patient's email
 * @returns {*} - response containing logo image URL
 */
export const fetchTeamLogoByEmail = (email) => {
	return authenticatedFetch(`/api/file/team/logoUrl/email?data=${encodeURIComponent(email)}`, { method: 'get' })
		.then(function(response) {
			if (response.status === 200) {
				return response.json()
			}
		})
		.then(function(data) {
			return data
		})
}

/**
 * Get team logo image URL by encrypted link.
 *
 * @param {string} encryptedData - encrypted data from the link
 * @returns {*} - response containing logo image URL
 */
export const fetchTeamLogoByEncryptedLink = (encryptedData) => {
	return authenticatedFetch(`/api/file/team/logoUrl/encrypted?data=${encodeURIComponent(encryptedData)}`, { method: 'get' })
		.then(function(response) {
			if (response.status === 200) {
				return response.json()
			}
		})
		.then(function(data) {
			return data
		})
}

/**
 * Get new records for current patient's dashboard
 *
 * @param {number} lastSeenRecordId - ID of the last record that was shown to the patient (if any)
 * @returns {*} - response containig a list of new records
 */
export const fetchPatientUpdates = (lastSeenRecordId) => {
	return authenticatedFetch(`/api/patientUpdates/${lastSeenRecordId}`, { method: 'get' })
        .then(function (response) {
            if (response.status === 200) {
                return response.json()
            }
        })
        .then(function (updates) {
			return updates
        })
}

/**
 * Mark a set of patient's dashboard records as read
 *
 * @param {object} ids - model containig a list of record IDs
 * @returns {*} - response indicating if the action was successful
 */
export const markPatientUpdateAsRead = (ids) => {
	return authenticatedFetch(`/api/patientUpdates`,
            {
                method: 'post',
                headers: { 'content-type': 'application/json' },
				body: JSON.stringify({ Ids : ids })
            })
        .then(function (response) {
            if (response.status === 200) {
                return response.json()
            }
        })
        .then(function (response) {
			return response
        })
}

/**
 * Get the state of patient's registration process
 *
 * @param {string} email - patient's email
 * @returns {*} - response indicating if the registration was finished
 */
export const fetchIsRegistrationFinished = (email) => {

    let url = '/api/patient/isRegistrationFinished';

    if (email) {
		url += `?email=${encodeURIComponent(email)}`;
    }

	return authenticatedFetch(url, { method: 'get' })
        .then(function (response) {
            if (response.status === 200) {
                return response.json()
            }
        })
        .then(function (response) {
			return response
        })
}

/**
 * Get info about the user who created the patient by email
 *
 * @param {string} email - patient's email
 * @param {string} token - security token
 * @returns {*} - response containig creator data model
 */
export const getCreatorInfoByEmail = (email, token) => {

    let url = `/api/patient/getCreatorInfoByEmail?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`;

	return authenticatedFetch(url, { method: 'get' })
        .then(function (response) {
            if (response.status === 200) {
                return response.json()
            }
        })
        .then(function (response) {
			return response
        })
}

/**
 * Get info about the user who created the current patient
 *
 * @returns {*} - response containig creator data model
 */
export const getCreatorInfo = () => {

    let url = `/api/patient/getCreatorInfo`;

	return authenticatedFetch(url, { method: 'get' })
        .then(function (response) {
            if (response.status === 200) {
                return response.json()
            }
        })
        .then(function (response) {
			return response
        })
}

/**
 * TODO: Temporary endpoint until the "go to registration" flow is reworked
 * Get info about who created the patient
 *
 * @param {string} email - patient's email
 * @returns {*} - response containig creator data model
 */
export const getCreatorInfoByEmailTemp = (email,) => {

    let url = `/api/patient/getCreatorInfoByEmailTemp?email=${encodeURIComponent(email)}`;

	return authenticatedFetch(url, { method: 'get' })
        .then(function (response) {
            if (response.status === 200) {
                return response.json()
            }
        })
        .then(function (response) {
			return response
        })
}

/**
 * Get info about user who created the patient by data from encrypted link
 *
 * @param {string} data - encrypted link
 * @returns {*} - response containig creator data model
 */
export const getCreatorInfoByEncryptedLink = (data) => {

    let url = `/api/patient/getCreatorInfoByLink?data=${encodeURIComponent(data)}`;

	return authenticatedFetch(url, { method: 'get' })
        .then(function (response) {
            if (response.status === 200) {
                return response.json()
            }
        })
        .then(function (response) {
			return response
        })
}

/**
 * Set the token for push messages for patient
 *
 * @param {string} deviceId - device ID
 * @param {string} pushToken - push token
 * @returns {*} - response indicating if the action was successful
 */
export const setPatientPushToken = (deviceId, pushToken) => {
	return authenticatedFetch(`/api/patient/pushToken/set`, {
		method: 'post',
		headers: { 'content-type': 'application/json' },
		body: JSON.stringify({
			deviceId: deviceId,
			pushToken: pushToken
		})
	})
		.then(function(response) {
			if (response.status === 200) {
				return response.json()
			}
		})
		.then(function(user) {
			return user
		})
}

/**
 * Send a request of patient's account and data deletion
 *
 * @param {number} id - patient ID
 * @returns {*} - response indicating if the request was sent successfully
 */
export const deletePatientAccount = (id) => {
	return authenticatedFetch(`/api/patient/${id}`, {
		method: 'delete'
	})
		.then(function(response) {
			if (response.status === 200) {
				return response.json()
			}
		})
		.then(function(user) {
			return user
		})
}

/**
 * Get the link for current patient registration with encrypted data in it
 *
 * @param {*} id - @obsolete
 * @returns {*} - response containing registration link
 */
export const getPatientRegistrationLink = (id) => {
	return authenticatedFetch(`/api/patient/registrationLink`, {
		method: 'GET'
	})
		.then(function(response) {
			if (response.status === 200) {
				return response.json()
			}
		})
		.then(function(user) {
			return user
		})
}

/**
 * Check if self-registration link has expired
 *
 * @param {string} data - encrypted data from the link
 * @returns {*} - response indicating if the link is still valid
 */
export const checkIfSelfRegistrationLinkHasExpired = (data) => {

    let url = `/api/patient/checkIfLinkHasExpired?data=${encodeURIComponent(data)}`;

	return authenticatedFetch(url, { method: 'get' })
        .then(function (response) {
            if (response.status === 200) {
                return response.json()
            }
        })
        .then(function (response) {
			return response
        })
}

/**
 * Log the email link click event by message ID
 *
 * @param {string} messageId - message ID
 * @returns {*} - response indicating if the action was successful
 */
export const setEmailClickByMessageId = (messageId) => {
	let url = `/api/patient/setEmailClickByMessageId/${messageId}`;

	return authenticatedFetch(url, { method: 'post' })
		.then(function (response) {
			if (response.status === 200) {
				return response.json()
			}
		})
		.then(function (response) {
			return response
		})
}

/**
 * Log the email link click event by patient email
 *
 * @param {string} email - patient email
 * @param {string} token - security token
 * @returns {*} - response indicating if the action was successful
 */
export const setEmailClickByPatientEmail = (email, token) => {
	let searchParams = {
		email,
		token
	}

	let url = `/api/patient/setEmailClickByPatientEmail?${new URLSearchParams(searchParams).toString()}`;

	return authenticatedFetch(url, { method: 'post' })
		.then(function (response) {
			if (response.status === 200) {
				return response.json()
			}
		})
		.then(function (response) {
			return response
		})
}

/**
 * Log the email link click event by case ID
 *
 * @param {number} caseId - case ID
 * @returns {*} - response indicating if the action was successful
 */
export const setEmailClickByCaseId = (caseId) => {
	let url = `/api/patient/setEmailClickByCaseId/${caseId}`;

	return authenticatedFetch(url, { method: 'post' })
		.then(function (response) {
			if (response.status === 200) {
				return response.json()
			}
		})
		.then(function (response) {
			return response
		})
}